<section class="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-surface-1/70">
  <div class="flex flex-wrap items-center justify-between gap-4 px-6 py-5">
    <h4 class="text-lg font-semibold text-[#1A1C21] dark:text-white">Unpaid Payments Overview</h4>
    <div class="flex flex-wrap items-center gap-3">
      <ng-content select="[overview-header-actions]"></ng-content>
      <div *ngIf="showAskPrismAi" class="rounded-lg bg-gradient-to-r from-fuchsia-500 to-cyan-400 p-px">
        <button
          type="button"
          class="inline-flex items-center gap-1.5 rounded-[7px] bg-white px-3 py-1.5 text-sm font-semibold text-[#1A1C21] transition hover:bg-slate-50 dark:bg-surface-1 dark:text-white"
        >
          <app-icon-v2 name="sparkles" size="16" class="text-fuchsia-500"></app-icon-v2>
          Ask Prism AI
        </button>
      </div>
    </div>
  </div>

  <div
    class="grid grid-cols-1 gap-5 px-6 pb-6 sm:grid-cols-2"
    [ngClass]="showUrgentAction ? 'xl:grid-cols-4' : 'xl:grid-cols-3'"
  >
    <!-- Card 1: Total Available Balance -->
    <article
      class="flex min-h-[188px] flex-col rounded-xl border border-slate-200 bg-white p-6 dark:border-slate-700/50 dark:bg-slate-800/30"
    >
      <div class="flex items-center gap-3">
        <div class="flex h-9 w-9 items-center justify-center rounded-lg bg-[#F2F4F7] text-[#667085] dark:bg-slate-800 dark:text-slate-400">
          <app-icon-v2 name="wallet" size="16"></app-icon-v2>
        </div>
        <p class="text-sm font-semibold text-[#667085] dark:text-slate-300">Total Available Balance</p>
      </div>
      @if (loading) {
        <div class="mt-5 h-9 w-44 animate-pulse rounded-md bg-[#dde3ea] dark:bg-surface-3"></div>
        <div class="mt-auto border-t border-slate-200 pt-4 dark:border-slate-700/50">
          <div class="h-3 w-20 animate-pulse rounded bg-[#dde3ea] dark:bg-surface-3"></div>
        </div>
      } @else {
        <p class="mt-5 text-[32px] font-bold leading-none tabular-nums text-[#1A1C21] dark:text-white">
          ${{ (data?.availableBalance ?? 0) | number : '1.2-2' }}
        </p>
        <div class="mt-auto border-t border-slate-200 pt-4 dark:border-slate-700/50">
          <p class="text-xs font-normal text-[#98A2B3] dark:text-slate-500">{{ overviewAsOfDate | date : 'MM/dd/yyyy' }}</p>
        </div>
      }
    </article>

    <!-- Card 2: Upcoming Amount -->
    <article class="flex min-h-[188px] flex-col rounded-xl bg-[#E9F7F2] p-6 dark:bg-teal-950/20">
      <div class="flex items-center gap-3">
        <div class="flex h-9 w-9 items-center justify-center rounded-lg bg-white text-[#0E9384] dark:bg-teal-900/50">
          <app-icon-v2 name="calendar" size="16"></app-icon-v2>
        </div>
        <p class="text-sm font-semibold text-[#344054] dark:text-teal-100">Upcoming Amount</p>
      </div>
      @if (loading) {
        <div class="mt-5 h-9 w-36 animate-pulse rounded-md bg-[#c6e9de] dark:bg-teal-900/40"></div>
        <div class="mt-auto border-t border-[#C6E9DE] pt-4 dark:border-teal-900/50">
          <div class="h-3 w-32 animate-pulse rounded bg-[#c6e9de] dark:bg-teal-900/40"></div>
        </div>
      } @else {
        <p class="mt-5 text-[32px] font-bold leading-none tabular-nums text-[#1A1C21] dark:text-white">
          ${{ (data?.upcomingAmount ?? 0) | number : '1.2-2' }}
        </p>
        <div class="mt-auto flex items-center gap-1.5 border-t border-[#C6E9DE] pt-4 dark:border-teal-900/50">
          <ng-container *ngIf="(data?.upcomingAmount ?? 0) > 0; else noUpcoming">
            <span class="text-xs font-normal text-[#667085] dark:text-teal-300/70">Approx. In 2 Days</span>
            <app-tooltip
              [content]="upcomingAmountTooltip"
              position="top"
              [showArrow]="true"
              [flexCenter]="true"
              [useFixedStrategy]="true"
              [delayShow]="150"
              maxWidth="300px"
              [zIndex]="overviewTooltipZIndex()"
              triggerClassName="!cursor-help inline-flex items-center"
            >
              <button
                type="button"
                class="inline-flex shrink-0 items-center justify-center rounded-full text-[#98A2B3] transition hover:text-[#667085] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[#0E9384] dark:text-teal-500 dark:hover:text-teal-300"
                aria-label="Upcoming amount details"
              >
                <app-icon-v2 name="info" size="14"></app-icon-v2>
              </button>
            </app-tooltip>
          </ng-container>
          <ng-template #noUpcoming>
            <span class="text-xs font-normal text-[#667085] dark:text-teal-300/70">No upcoming balance</span>
          </ng-template>
        </div>
      }
    </article>

    <!-- Card 3: Amount To Pay -->
    <article
      class="flex flex-col rounded-[14px] bg-[#e9f4ff] p-5 dark:bg-blue-500/15"
      [ngClass]="showPayAllAction ? 'min-h-[206px]' : 'min-h-[188px]'"
    >
      <div class="flex h-full flex-col">
        <div class="flex items-center gap-3">
          <span
            class="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white shadow-sm dark:bg-surface-3"
          >
            <app-icon-v2
              name="circleDollarSign"
              class="h-5 w-5 text-[#0c66e8] dark:text-[#9cc5ff]"
              size="20"
            ></app-icon-v2>
          </span>
          <p class="text-[15px] font-semibold text-[#38385b] dark:text-white">Amount To Pay</p>
        </div>

        @if (loading || calculating) {
          <div class="mt-4 h-9 w-40 animate-pulse rounded-md bg-[#d0e4f7] dark:bg-white/10"></div>
          <div class="mt-auto" [ngClass]="showPayAllAction ? 'pt-4' : 'pt-8'">
            <div class="border-t border-[#d8e8f8] dark:border-line"></div>
            <div class="mt-3.5 h-4 w-24 animate-pulse rounded bg-[#d0e4f7] dark:bg-white/10"></div>
            @if (showPayAllAction) {
              <div class="mt-3 h-11 w-full animate-pulse rounded-[6.75px] bg-[#d0e4f7] dark:bg-white/10"></div>
            }
          </div>
        } @else {
          @if (!hideAmount) {
            <div class="mt-4 text-[32px] font-bold leading-[1.2] tracking-[-0.02em] text-[#141437] dark:text-white">
              {{ (data?.totalPaymentToPay ?? 0) | currency }}
            </div>
          }

          <div class="mt-auto" [ngClass]="showPayAllAction ? 'pt-4' : 'pt-8'">
            @if (!hideAmount) {
              <div class="border-t border-[#d8e8f8] dark:border-line"></div>
              <p class="mt-3.5 text-[14px] font-medium text-[#7d7d93] dark:text-ink-muted">
                {{ data?.totalPaymentsCount ?? 0 }} Payments
              </p>
            }
            @if (showPayAllAction) {
              <button
                type="button"
                class="mt-3 inline-flex h-11 w-full items-center justify-center rounded-[6.75px] bg-[#0c66e8] px-3.5 py-[7px] text-sm font-bold leading-[1.4] text-white transition hover:bg-[#0a5ad0] disabled:cursor-not-allowed disabled:opacity-50 dark:bg-[#0c66e8] dark:hover:bg-[#0a5ad0]"
                [disabled]="payAllDisabled"
                (click)="payAllToBusiness.emit()"
              >
                {{ payAllLabel }}
              </button>
            }
          </div>
        }
      </div>
    </article>

    <!-- Card 4: Urgent Payments -->
    <article
      *ngIf="showUrgentAction"
      class="flex min-h-[188px] flex-col rounded-xl bg-[#FAF5FF] p-6 dark:bg-fuchsia-950/20"
    >
      <div class="flex items-center gap-3">
        <div class="flex h-9 w-9 items-center justify-center rounded-lg bg-white text-[#C026D3] dark:bg-fuchsia-900/50">
          <app-icon-v2 name="zap" size="16"></app-icon-v2>
        </div>
        <p class="text-sm font-semibold text-[#344054] dark:text-fuchsia-100">Urgent Payments</p>
      </div>
      @if (loading || calculating) {
        @if (hideUrgentAmount) {
          <div class="mt-5 h-9 w-36 animate-pulse rounded-md bg-[#ead5f7] dark:bg-fuchsia-900/40"></div>
        } @else {
          <div class="mt-5 h-9 w-36 animate-pulse rounded-md bg-[#ead5f7] dark:bg-fuchsia-900/40"></div>
          <div class="mt-2 h-4 w-24 animate-pulse rounded bg-[#ead5f7] dark:bg-fuchsia-900/40"></div>
        }
        <div class="mt-auto h-10 w-full animate-pulse rounded-lg bg-[#ead5f7] dark:bg-fuchsia-900/40"></div>
      } @else {
        @if (hideUrgentAmount) {
          <p class="mt-5 text-[32px] font-bold leading-none tabular-nums text-[#1A1C21] dark:text-white">
            {{ data?.urgentPaymentsCount ?? 0 }} Payments
          </p>
        } @else {
          <p class="mt-5 text-[32px] font-bold leading-none tabular-nums text-[#1A1C21] dark:text-white">
            ${{ (data?.urgentPaymentsAmount ?? 0) | number : '1.2-2' }}
          </p>
          <p class="mt-1 text-sm font-normal text-[#667085] dark:text-fuchsia-300/70">{{ data?.urgentPaymentsCount ?? 0 }} Payments</p>
        }
        <button
          type="button"
          class="mt-auto inline-flex w-full items-center justify-center rounded-lg bg-[#C026D3] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#A21CAF] disabled:cursor-not-allowed disabled:opacity-50 dark:bg-[#d946ef] dark:hover:bg-[#c026d3]"
          [disabled]="payUrgentDisabled"
          (click)="payUrgent.emit()"
        >
          Pay Urgent
        </button>
      }
    </article>
  </div>
</section>
